import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Minus, ArrowRight, Phone } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [openItems, setOpenItems] = useState(new Set([0]));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const res = await axios.get(`${API}/faq`);
        setFaqs(res.data);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 container-padding">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-caption text-[#D4AF37] mb-4">FAQ</p>
          <h1 className="heading-display text-[#1C1C1C] mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-body-lg text-[#66605B]">
            Everything you need to know about staying at Big Bear Estate. 
            Can't find your answer? Feel free to contact us.
          </p>
        </motion.div>
      </section>

      {/* FAQ Accordion */}
      <section className="container-padding pb-20">
        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, idx) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              className="border-b border-[#E2E0DC]"
            >
              <button
                onClick={() => toggleItem(idx)}
                className="w-full py-6 flex items-start justify-between gap-4 text-left"
                data-testid={`faq-item-${idx}`}
              >
                <span className="font-serif text-lg text-[#1C1C1C] pr-4">
                  {faq.question}
                </span>
                <span className="flex-shrink-0 mt-1">
                  {openItems.has(idx) ? (
                    <Minus className="w-5 h-5 text-[#1B4D3E]" />
                  ) : (
                    <Plus className="w-5 h-5 text-[#1B4D3E]" />
                  )}
                </span>
              </button>
              
              <motion.div
                initial={false}
                animate={{
                  height: openItems.has(idx) ? 'auto' : 0,
                  opacity: openItems.has(idx) ? 1 : 0,
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <p className="pb-6 text-[#66605B] leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-[#F0EBE5] section-padding">
        <div className="container-padding">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-serif text-3xl text-[#1C1C1C] mb-4">
              Still Have Questions?
            </h2>
            <p className="text-[#66605B] mb-8">
              Our property manager is happy to help with any questions about 
              Big Bear Estate. Don't hesitate to reach out.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:416-543-4749"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" />
                Call 416.543.4749
              </a>
              <Link
                to="/booking"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                Send Inquiry
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="section-padding">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-serif text-2xl text-[#1C1C1C] mb-8 text-center">
              Helpful Resources
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/amenities"
                className="group p-6 bg-white rounded-sm shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="font-serif text-lg text-[#1C1C1C] mb-2 group-hover:text-[#1B4D3E] transition-colors">
                  View Amenities
                </h3>
                <p className="text-sm text-[#66605B]">
                  Discover all the features and amenities included in your stay.
                </p>
              </Link>
              <Link
                to="/pricing"
                className="group p-6 bg-white rounded-sm shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="font-serif text-lg text-[#1C1C1C] mb-2 group-hover:text-[#1B4D3E] transition-colors">
                  Pricing & Terms
                </h3>
                <p className="text-sm text-[#66605B]">
                  View seasonal rates, deposit requirements, and booking terms.
                </p>
              </Link>
              <Link
                to="/gallery"
                className="group p-6 bg-white rounded-sm shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="font-serif text-lg text-[#1C1C1C] mb-2 group-hover:text-[#1B4D3E] transition-colors">
                  Photo Gallery
                </h3>
                <p className="text-sm text-[#66605B]">
                  Take a virtual tour through our stunning lakefront property.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
